import ClientSheetResponseResource from "common/resources/ClientSheet/ClientSheetResponseResource";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonVariant } from "components/elements/Button";
import I18n from "components/materials/I18n";
import SelectClientTable, { EClientValidation } from "components/materials/SelectClientTable";
import PageTemplateSubscriptions from "components/PageTemplateSubscriptions";
import ModuleConfig from "configs/ModuleConfig";
import useOpenable from "hooks/useOpenable";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientService from "services/ClientService";
import { container } from "tsyringe";

import AddSpouseModal from "../modals/AddSpouseModal";
import EditClientModal from "../modals/EditClientModal";
import classes from "./classes.module.scss";

const moduleConfig = container.resolve(ModuleConfig);
const clientService = container.resolve(ClientService);
const FIXED_FOOTER_BOTTOM_MARGIN = 160;

export default function SelectClient() {
	const navigate = useNavigate();
	const [selectedClient, setSelectedClient] = useState<ClientSheetResponseResource | null>(null);
	const [spouse, setSpouse] = useState<ClientSheetResponseResource | null>(null);
	const [selectedClientErrors, setSelectedClientErrors] = useState<ValidationError[]>([]);
	const [spouseErrors, setSpouseErrors] = useState<ValidationError[]>([]);

	const [isCoSubscription, setIsCoSubscription] = useState<boolean>(false);

	const coSubscriberModal = useOpenable();
	const clientNotValidModal = useOpenable();
	const spouseNotValidModal = useOpenable();

	const launchSubscription = useCallback(
		(client: ClientSheetResponseResource | null) => {
			setSelectedClient(client);
			setIsCoSubscription(false);
			setSelectedClientErrors([]);

			if (!client) return console.warn("No client selected");

			clientNotValidModal.open(); // Toujours ouvrir la popup
		},
		[clientNotValidModal],
	);

	const lauchCoSubscription = useCallback(
		(client: ClientSheetResponseResource | null) => {
			setSelectedClient(client);
			setIsCoSubscription(true);
			setSelectedClientErrors([]);
			setSpouseErrors([]);

			if (!client) return console.warn("No client selected");

			clientNotValidModal.open(); // Toujours ouvrir la popup
		},
		[clientNotValidModal],
	);

	const navigateToAddSpouse = useCallback(() => {
		if (!selectedClient) return console.warn("No client selected");
		navigate(moduleConfig.get().modules.pages.Subscriptions.props.pages.AddSpouseToClient.props.path.replace(":clientSheetId", selectedClient.id));
	}, [selectedClient, navigate]);

	const navigateToSubscriptions = useCallback(() => {
		navigate(moduleConfig.get().modules.pages.Subscriptions.props.pages.InProgress.props.path);
	}, [navigate]);

	const onEditSuccess = useCallback(
		(client: ClientSheetResponseResource) => {
			if (!client) return;

			if (isCoSubscription) {
				const spouseId = client.spouseId;
				if (!spouseId) {
					// Pas de conjoint → ouvrir AddSpouseModal
					return coSubscriberModal.open();
				}

				clientService.getClientSheetById(spouseId).then((spouse) => {
					setSpouse(spouse);
					// Toujours ouvrir le modal du conjoint, même s'il est valide
					spouseNotValidModal.open();
				});
			} else {
				// Aller directement à l'étape suivante
				navigate(
					moduleConfig.get().modules.pages.Subscriptions.props.pages.SelectProducts.props.path.replace(":clientSheetId", client.id!).concat("?isCoSubscription=false"),
				);
			}
		},
		[isCoSubscription, coSubscriberModal, navigate, spouseNotValidModal],
	);

	const onEditSpouseSuccess = useCallback(
		(spouse: ClientSheetResponseResource) => {
			const selectedClientSheetId = spouse.spouseId;
			if (!selectedClientSheetId) return console.warn("No selectedClientSheetId found after editing spouse");

			navigate(
				moduleConfig
					.get()
					.modules.pages.Subscriptions.props.pages.SelectProducts.props.path.replace(":clientSheetId", selectedClientSheetId)
					.concat("?isCoSubscription=true"),
			);
		},
		[navigate],
	);

	return (
		<PageTemplateSubscriptions
			mainPageTitle={I18n.asset.common.back}
			headerTitle={I18n.asset.pages.subscriptions.existing_clients.title}
			overridePreviousClick={{ onClick: navigateToSubscriptions, text: I18n.asset.common.back_to_subscriptions }}>
			<div className={classes["root"]}>
				<SelectClientTable onSelectClient={setSelectedClient} clientValidation={EClientValidation.COMPLETED} viewportBottomMargin={FIXED_FOOTER_BOTTOM_MARGIN} />

				<div className={classes["footer"]}>
					<Button variant={EButtonVariant.OUTLINED} onClick={() => navigate(-1)}>
						{I18n.asset.common.cancel}
					</Button>
					<Button variant={EButtonVariant.OUTLINED} disabled={!selectedClient} onClick={() => lauchCoSubscription(selectedClient)}>
						{I18n.asset.pages.subscriptions.existing_clients.launch_co_subscription}
					</Button>
					<Button disabled={!selectedClient} onClick={() => launchSubscription(selectedClient)}>
						{I18n.asset.pages.subscriptions.existing_clients.launch_subscription}
					</Button>
				</div>
			</div>

			<AddSpouseModal isOpen={coSubscriberModal.isOpen} onClose={coSubscriberModal.close} onClick={navigateToAddSpouse} />
			<EditClientModal
				isOpen={clientNotValidModal.isOpen}
				onClose={clientNotValidModal.close}
				onEditSuccess={onEditSuccess}
				clientToEdit={selectedClient}
				title={I18n.asset.pages.subscriptions.existing_clients.modal_verify_client.title}
				subtitle={I18n.asset.pages.subscriptions.existing_clients.modal_verify_client.subtitle}
				defaultErrors={selectedClientErrors}
			/>
			<EditClientModal
				isOpen={spouseNotValidModal.isOpen}
				onClose={spouseNotValidModal.close}
				onEditSuccess={onEditSpouseSuccess}
				clientToEdit={spouse}
				title={I18n.asset.pages.subscriptions.existing_clients.modal_verify_client.title}
				subtitle={I18n.asset.pages.subscriptions.existing_clients.modal_verify_client.subtitle}
				defaultErrors={spouseErrors}
			/>
		</PageTemplateSubscriptions>
	);
}
